import { listBriefs } from '@frontend/components/briefs/briefsApi';
import { BRIEF_PICKER_WIDGET_TYPE, briefPickerOptions } from '@frontend/store/creative-studio/brief-picker';
import { draftScript } from '@frontend/services/video-scenes/script-draft-service';
import { setPendingVideoCreate } from '@frontend/utils/video-import-handoff';

/**
 * "Create a video" in AI Studio (POR-14827). The user picks a brief, or describes
 * the video; the video editor then opens its New Project popup on that brief's
 * setup step, or on the script drafted from the description. The video itself is
 * created there, where the scenes, format and credits are reviewed first.
 */

export const VIDEO_EDITOR_URL = '/video';

// The brief picker's pick action for a video, as `video_brief_pick:<brief id>`.
export const VIDEO_BRIEF_PICK_ACTION = 'video_brief_pick';

const activeConversationId = (state) => (state.activeConversation ? state.activeConversation.id : null);

const escapeHtml = (text) => String(text)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

function parseArguments($message) {
  try {
    const args = $message.function_call.arguments;

    return (typeof args === 'string' ? JSON.parse(args) : args) || {};
  } catch (e) {
    return {};
  }
}

/** Hands the request to the video page and says so; the caller navigates to `redirectUrl`. */
async function openVideoEditor(request, content, { dispatch }) {
  if (!setPendingVideoCreate(request)) {
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: "I couldn't open the video editor. Please try again.",
    });

    return null;
  }

  await dispatch('addAndSaveMessage', { role: 'assistant', content });

  return { redirectUrl: VIDEO_EDITOR_URL };
}

/** Asks for the brief or the description; the reply is handled by sendMessage's pendingVideoDescribe branch. */
export async function askForVideoSource({ state, commit, dispatch }) {
  const conversationId = activeConversationId(state);
  let briefs = [];

  try {
    const res = await listBriefs({ per_page: 100 }, { internal: true });
    briefs = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
  } catch (e) {
    briefs = [];
  }

  if (activeConversationId(state) !== conversationId) {
    return;
  }

  await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: briefs.length
      ? "Let's make a video. Pick a brief to plan it from, or describe the video you want and I'll draft the scenes."
      : "Let's make a video. Describe what it should show and I'll draft the scenes.",
    widgets: briefs.length
      ? [{
        type: BRIEF_PICKER_WIDGET_TYPE,
        id: 'video_brief_picker',
        action: VIDEO_BRIEF_PICK_ACTION,
        options: briefPickerOptions(briefs),
      }]
      : [],
  });

  commit('SET_PENDING_VIDEO_DESCRIBE', { conversationId });
}

/** Drafts a script from the user's description and opens it in the video editor. */
export async function createVideoFromDescription(description, { state, commit, dispatch }) {
  const conversationId = activeConversationId(state);
  const lastUserMessage = [...(state.messages || [])].reverse().find((m) => m && m.role === 'user');
  const hadAttachments = !!(lastUserMessage && (lastUserMessage.attachments || []).length);
  const tempId = `tmp_video_draft_${Date.now()}`;

  commit('ADD_MESSAGE', { role: 'assistant', content: 'Drafting the scenes for your video…', tempId });

  let text = '';

  try {
    ({ text } = await draftScript({ description }));
  } catch (error) {
    commit('REMOVE_MESSAGE', { tempId });

    const reason = (error && error.response && error.response.data && error.response.data.message)
      || "I couldn't draft a script from that.";

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: `${escapeHtml(reason)} Describe the video again and I'll retry.`,
    });

    if (activeConversationId(state) === conversationId) {
      commit('SET_PENDING_VIDEO_DESCRIBE', { conversationId });
    }

    return null;
  }

  commit('REMOVE_MESSAGE', { tempId });

  // The user left this chat while the script was drafted: don't take them away from the one they are in.
  if (activeConversationId(state) !== conversationId) {
    return null;
  }

  const sceneCount = text.split('\n').filter((line) => line.trim()).length;
  const attachmentsNote = hadAttachments
    ? ' Attached images aren\'t carried over — add them to the scenes there.'
    : '';

  return openVideoEditor(
    { source: 'script', text },
    `I drafted ${sceneCount} scene${sceneCount === 1 ? '' : 's'} for your video. Opening the video editor — `
      + `review the script there, then create the video.${attachmentsNote}`,
    { dispatch },
  );
}

/** Opens the video editor on the brief the user picked. */
export function createVideoFromBrief({ briefId, briefTitle }, { dispatch }) {
  return openVideoEditor(
    { source: 'brief', briefId: String(briefId), briefTitle: briefTitle || '' },
    briefTitle
      ? `Opening the video editor with the <b>${escapeHtml(briefTitle)}</b> brief…`
      : 'Opening the video editor with your brief…',
    { dispatch },
  );
}

/**
 * create_video: with a description, draft it straight away; without one, ask
 * for a brief or a description.
 */
export const handleCreateVideo = async ($message, context) => {
  const description = String(parseArguments($message).description || '').trim();

  if (description) {
    return createVideoFromDescription(description, context);
  }

  await askForVideoSource(context);

  return null;
};
